import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useSnapEngage} from '../../custom-api/hooks/use-snap-engage'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import {useLocation} from 'react-router-dom'
import {Global, css} from '@emotion/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'

const HIDE_CSS = css`
    #designstudio-button {
        display: none !important;
    }
`

const getShouldDisableChat = (pathname, exclusions = []) => {
    for (const exclusion of exclusions) {
        try {
            const regex = new RegExp(exclusion)
            if (regex.test(pathname)) {
                return true
            }
        } catch (e) {
            if (pathname === exclusion) {
                return true
            }
        }
    }

    return false
}

const SnapEngage = () => {
    const location = useLocation()
    const {data: customer} = useCurrentCustomer()
    const snapEngageConfig = useCustomPreferenceWithLocale('snapEngageConfiguration')
    const snapEngageWidgetID = snapEngageConfig?.snapEngageWidgetID || ''
    const proactiveChat =
        snapEngageConfig?.proactiveChat === 'true' || snapEngageConfig?.proactiveChat === true
    const snapEngageURLExclusion = useMemo(
        () => snapEngageConfig?.snapEngageURLExclusion || [],
        [snapEngageConfig],
    )
    const shouldDisableChat = useMemo(
        () => getShouldDisableChat(location.pathname, snapEngageURLExclusion),
        [location.pathname, snapEngageURLExclusion],
    )
    const shouldAllowChat = !shouldDisableChat

    const [hide, setHide] = useState(false)
    const lastVisibilityActionRef = useRef(null)

    const {addLayer} = useGoogleTagManager()

    useEffect(() => {
        let interval = null

        function registerCallbacks() {
            if (window.SnapEngage && window.SnapEngage.setCallback) {
                ;['Open'].forEach((event) => {
                    window.SnapEngage.setCallback(event, () => {
                        if (event === 'Open') {
                            addLayer(
                                basic_gtm_event.bind(this, {
                                    event: 'chat',
                                    eventCategory: 'engagement',
                                    eventAction: 'chat',
                                    eventLabel: 'open',
                                }),
                            )

                            window.SnapEngage.setCustomField('CRM_name', customer?.firstName || '')
                            window.SnapEngage.setCustomField(
                                'CRM_surname',
                                customer?.lastName || '',
                            )
                            window.SnapEngage.setUserEmail(customer?.email || '')
                        }
                        setHide(event === 'Open')
                    })
                })
                clearInterval(interval)
            }
        }

        registerCallbacks()
        interval = setInterval(registerCallbacks, 500)

        return () => clearInterval(interval)
    }, [customer])

    useSnapEngage({snapEngageWidgetID, proactiveChat})

    useEffect(() => {
        lastVisibilityActionRef.current = null
    }, [location.pathname])

    useEffect(() => {
        const applySnapEngageVisibility = () => {
            if (!window.SnapEngage) {
                return false
            }

            window.SnapEngage.allowProactiveChat(shouldAllowChat)

            const visibilityAction = shouldDisableChat ? 'hide' : 'show'
            if (lastVisibilityActionRef.current === visibilityAction) {
                return true
            }

            if (shouldDisableChat) {
                if (typeof window.SnapEngage.hideButton === 'function') {
                    try {
                        window.SnapEngage.hideButton()
                        lastVisibilityActionRef.current = 'hide'
                    } catch (e) {
                        console.warn('SnapEngage.hideButton() error:', e)
                    }
                }
            } else {
                if (typeof window.SnapEngage.showButton === 'function') {
                    try {
                        window.SnapEngage.showButton()
                        lastVisibilityActionRef.current = 'show'
                    } catch (e) {
                        console.warn('SnapEngage.showButton() error:', e)
                    }
                }
            }

            return true
        }

        if (applySnapEngageVisibility()) {
            return
        }

        const interval = setInterval(() => {
            if (applySnapEngageVisibility()) {
                clearInterval(interval)
            }
        }, 250)

        return () => clearInterval(interval)
    }, [location.pathname, shouldAllowChat, shouldDisableChat, snapEngageURLExclusion])

    return <>{(shouldDisableChat || hide) && <Global styles={HIDE_CSS} />}</>
}

export default SnapEngage
